import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { useRouter } from 'next/router';
import * as variables from 'pubweb-smokey/dist/components/GridSystem/_vars_widths.js';

import Modal from '@components/Shared/Modal/Modal';
import { modelNameTransformer } from 'pubweb-smokey/dist/utils/modelNameTransformer';
import { getLocalStorage } from 'pubweb-smokey/dist/utils/utils';
import { getSiteUrl } from '@utils/config';
import LazyLoad from '@components/Shared/LazyLoad/LazyLoad';
import RequestInfo from '@components/FindAHomeDetails/RequestInfo';

import { LocationsContext } from '@contexts/LocationsContext';
import * as tokenService from '@services/tokenService';

import HeadingMobile from '@components/FindAHomeDetails/HeadingMobile';
import HeadingDesktop from '@components/FindAHomeDetails/HeadingDesktop';
import Slideshow from '@components/Shared/Slideshow/Slideshow';
import ActionButtons from '@components/FindAHomeDetails/ActionButtons';
import Features from '@components/FindAHomeDetails/Features/Features';
import FindARetailer from '@components/Shared/FindARetailer/FindARetailer';
import StickyNav from '@components/FindAHomeDetails/StickyNav';
import EmailSignupContainer from '@components/Shared/EmailSignupContainer/EmailSignupContainer';
import { HomeDetailsStyles } from './HomeDetails.styled';
import { DXAccountContext } from '@contexts/DXAccountContext';
import {
  getModelByModelNumber,
  insertModelIntoRecentlyViewed,
} from '@services/homesService';
import { isHomeInStock } from '@components/HomeDetails/isHomeInStock';
import { epicHomes } from '@components/HomeDetails/data';
import DynamicDisclaimer from '@components/Shared/Disclaimer/DynamicDisclaimer';
import Colors from 'pubweb-smokey/dist/colors';

export const HomeDetails = ({ homeDetails }) => {
  const [showInfoRequestForm, setShowInfoRequestForm] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [resizeTimeout, setResizeTimeout] = useState(false);
  const [windowWidth, setWindowWidth] = useState(0);
  const [price, setPrice] = useState();
  const [isPriceLoading, setIsPriceLoading] = useState(true);
  const [isInStock, setIsInStock] = useState(false);
  const [displaySticky, setDisplaySticky] = useState(false);
  const [recentHomesUpdated, setRecentHomesUpdated] = useState(false);
  const [emailSignup, setEmailSignup] = useState(false);

  const visitorToken = tokenService.getVisitorToken();
  const locationsContext = useContext(LocationsContext);
  const accountContext = useContext(DXAccountContext);
  const router = useRouter();

  const isDesktop =
    typeof window !== 'undefined' && windowWidth > variables.desktop_breakpoint;

  const epicSlug = React.useMemo(() => {
    const epicHome = epicHomes.find(({ id }) => id === homeDetails.modelNumber);

    return epicHome ? epicHome.slug : false;
  }, [homeDetails]);

  const updateRecentlyViewed = () => {
    let geolocation = locationsContext.state.geocode;
    let { distance } = locationsContext.state;

    insertModelIntoRecentlyViewed(
      homeDetails.modelId,
      visitorToken,
      geolocation.latitude,
      geolocation.longitude,
      distance
    ).then((result) => {
      if (typeof visitorToken === 'undefined' || visitorToken === null) {
        tokenService.setVisitorToken(result.visitorToken);
        setRecentHomesUpdated(true);
      }
    });
  };

  const getPriceAndStockInfo = () => {
    // Unfortunately, this is necessary right now because we need to provide a location
    // to getModelByModelNumber() for pricing and inStock status, but
    // the locationContext isn't available in getInitialProps when we make the first call
    if (
      locationsContext &&
      locationsContext.state &&
      locationsContext.state.geocode
    ) {
      getModelByModelNumber(
        homeDetails.modelNumber,
        locationsContext.state.geocode.latitude,
        locationsContext.state.geocode.longitude,
        locationsContext.state.distance
      ).then((result) => {
        setIsPriceLoading(false);
        if (
          result !== null &&
          result.dealers !== null &&
          result.dealers.length > 0
        ) {
          setIsInStock(isHomeInStock(result.dealers));
          setPrice(result.startingInThePrice);
        } else {
          setIsInStock(false);
          setPrice(null);
        }
      });
    }
  };

  const handleRequestInfoClick = (formObj) => {
    setShowInfoRequestForm(false);
    if (formObj != null) {
      setShowLoginForm(accountContext.state?.email);
      setShowSignupForm(!accountContext.state?.email);
    }
  };

  const checkSticky = () => {
    // eslint-disable-next-line prefer-destructuring
    const headingElement = document.getElementsByClassName(
      'home-details-heading'
    )[0];

    const rect = headingElement.getBoundingClientRect();

    const elementOnScreen = rect.top < window.innerHeight && rect.bottom >= 0;

    if (!elementOnScreen && !displaySticky) {
      setDisplaySticky(true);
    } else if (elementOnScreen && !displaySticky) {
      setDisplaySticky(false);
    }
  };

  const scrollToContainer = (idName) => {
    //So, yes, this is overkill when an anchor tag would work, but I am having an issue with anchor links not navigating
    //correctly. Something to investigate later! - John.net
    //got this working properly -- the lazy load around the find a retailer section was breaking it bc the function couldn't
    //find the id of the container -- Jamie.html
    const yOffset = isDesktop ? -85 : -20;

    let ele = document.getElementById(idName);
    if (ele) {
      const y = ele.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (getLocalStorage('emailPopupClosed') !== true) {
        setEmailSignup(true);
      }
    }, 10000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    getPriceAndStockInfo();
    if (locationsContext.state.geocode !== null && !recentHomesUpdated) {
      updateRecentlyViewed();
    }
  }, [locationsContext.state.geocode, locationsContext.state.distance]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setWindowWidth(
        window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth
      );
    }

    const handleWindowResize = (e) => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      setResizeTimeout(
        setTimeout(() => {
          setWindowWidth(e.target.outerWidth);
        }, 500)
      );
    };
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('scroll', checkSticky);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('scroll', checkSticky);
    };
  }, []);

  return (
    <HomeDetailsStyles
      $hasVirtualTour={
        homeDetails.virtualTours[0]?.virtualTourReference ? true : false
      }
    >
      <Head htmlAttributes={{ lang: 'en' }}>
        <title>
          {modelNameTransformer(homeDetails.modelDescription)} | Clayton Homes
        </title>
        <meta
          name="description"
          content={`View ${modelNameTransformer(
            homeDetails.modelDescription
          )} floor plan, price, square feet and where to buy! Start your home search by requesting more information.`}
        />
        <link rel="canonical" href={`${getSiteUrl(router.pathname)}`} />
        <meta property="og:image" content={homeDetails.images[0].reference} />
      </Head>

      {emailSignup ? (
        <EmailSignupContainer
          enableShowDx={false}
          pageType="homedetail"
          onCloseEmailPopup={() => {
            setEmailSignup(false);
          }}
          modelNumber={homeDetails.modelNumber}
        />
      ) : null}

      {showInfoRequestForm && (
        <div className="request-form">
          <Modal onClose={() => setShowInfoRequestForm(false)}>
            <RequestInfo
              homeDetails={homeDetails}
              onClickOkay={handleRequestInfoClick}
              formOpen={showInfoRequestForm}
            />
          </Modal>
        </div>
      )}

      {accountContext.state.account === null &&
        (showLoginForm || showSignupForm) &&
        accountContext.actions.accountLogin}

      {displaySticky ? (
        <StickyNav
          homeDetails={homeDetails}
          onFeaturesClick={() => {
            scrollToContainer('features-container');
          }}
          onFindRetailerClick={() => {
            scrollToContainer('find-retailer');
          }}
          onRequestInfoClick={() => setShowInfoRequestForm(true)}
          epicSlug={epicSlug}
        />
      ) : null}

      <div className="constrained-width">
        {/* This is a lot easier than rearranging the order of the page with CSS.
              Each scenario requires different nesting and it would have been a nightmare! -Bonnie */}
        {isDesktop === false ? (
          <HeadingMobile
            homeDetails={homeDetails}
            price={price}
            isInStock={isInStock}
          />
        ) : (
          <HeadingDesktop
            homeDetails={homeDetails}
            price={price}
            isPriceLoading={isPriceLoading}
            onRequestInfoClick={() => {
              setShowInfoRequestForm(true);
            }}
            onFindRetailerClick={() => scrollToContainer('find-retailer')}
            isInStock={isInStock}
            epicSlug={epicSlug}
          />
        )}

        <LazyLoad>
          <div id="home-details-slides" className="slideshow-container">
            <Slideshow
              transitionTime={0}
              imageArray={homeDetails.images}
              virtualTourReference={
                homeDetails.virtualTours[0]?.virtualTourReference
              }
              isDesktop={isDesktop}
              viewportWidth={windowWidth}
            />
            <p id="imageDisclaimer" className="caption disclaimer">
              *Images may show options not included in base price
            </p>
          </div>
        </LazyLoad>

        {/* On desktop, the ActionButtons live in the <HeadingDesktop />, way at the top of the page. */}
        {isDesktop === false && (
          <ActionButtons
            onRequestInfoClick={() => {
              setShowInfoRequestForm(true);
            }}
            onFindRetailerClick={() => scrollToContainer('find-retailer')}
            epicSlug={epicSlug}
          />
        )}

        {homeDetails.tags && homeDetails.tags.length > 0 ? (
          <LazyLoad>
            <div className="features-container" id="features-container">
              <Features featuresArray={homeDetails.tags} />
            </div>
          </LazyLoad>
        ) : (
          isDesktop === false && (
            <div
              className="features-container-empty"
              id="features-container-empty"
            ></div>
          )
        )}
      </div>
      {/* /end constrained-width container */}

      <div className="bottom-page-container">
        <div className="bottom-page-info">
          <div className="find-retailer" id="find-retailer">
            <FindARetailer modelNumber={homeDetails.modelNumber} />
          </div>

          {/* Removed LazyLoad around disclaimer because it was not always loaded and could produce an error if the 'see-below' link in heading was clicked */}
          <DynamicDisclaimer
            id="disclaimer"
            textColor={`${Colors.accent.grey1.standard}`}
            backgroundColor={`${Colors.accent.grey7.standard}`}
            disclaimerContent="\* Advertised starting sales prices are for the home only. Delivery and installation costs are not included unless otherwise stated.
            Starting prices shown on this website are subject to change, see your local Home Center for current and specific home and pricing information.
            Sales price does not include other costs such as taxes, title fees, insurance premiums, filing or recording fees, land or improvements to the land,
            optional home features, optional delivery or installation services, wheels and axles, community or homeowner association fees, or any other items not
            shown on your Sales Agreement, Retailer Closing Agreement and related documents (your SA/RCA). If you purchase a home, your SA/RCA will show the details
            of your purchase. Homes available at the advertised sales price will vary by retailer and state. Artists’ renderings of homes are only representations
            and actual home may vary. Floor plan dimensions are approximate and based on length and width measurements from exterior wall to exterior wall.
            We invest in continuous product and process improvement. All home series, floor plans, specifications, dimensions, features, materials, and availability
            shown on this website are subject to change. Images may show options not included in base price. Does not include required taxes, delivery, and installation. Installed price will be higher.
            <br><br>**[ZERH Logo Use Guidelines (energy.gov)](https://www.energy.gov/sites/default/files/2022-11/ZERH%20Name%20and%20Logo%20Use%20Guidelines_0.pdf)"
          />
        </div>
      </div>
    </HomeDetailsStyles>
  );
};

HomeDetails.propTypes = {
  homeDetails: PropTypes.shape({
    images: PropTypes.any,
    modelDescription: PropTypes.any,
    modelId: PropTypes.any,
    modelNumber: PropTypes.any,
    tags: PropTypes.any,
    virtualTours: PropTypes.any,
  }),
};

export default HomeDetails;
